import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {DataService} from '../../services/data.service';
import {Budget} from '../../interfaces';
import {SummaryBalanceWrapper} from '../../interfaces/summaryBalance';
import {CommonModule} from '@angular/common';
import {MatCardModule} from '@angular/material/card';
import {SummaryBalanceV1Component} from './v1/summary-balance-component-v1.component';
import {SummaryBalanceV2Component} from './v2/summary-balance-component-v2.component';
import {ActivityBalanceTableComponent} from '../components/activity-balance-table/activity-balance-table.component';
import {MaterialsBalanceTableComponent} from '../components/materials-balance-table/materials-balance-table.component';
import {TimestampPipe} from '../../shared/pipes/timestamp.pipe';
import {BudgetListTableService} from '../../services/budget-list-table.service';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';


@Component({
    selector: 'app-summary-component',
    templateUrl: './summary-component.html',
    styleUrls: ['./summary-component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        MatCardModule,
        TimestampPipe,
        SummaryBalanceV1Component,
        SummaryBalanceV2Component,
        ActivityBalanceTableComponent,
        MaterialsBalanceTableComponent,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule
    ]
})
export class SummaryComponent implements OnInit, OnDestroy {

    public budgetId: string;
    public budget$: Observable<Budget>;
    public summaryBalance$: ReplaySubject<SummaryBalanceWrapper> = new ReplaySubject(1);
    private unsubscribe$ = new Subject();

    public hasPreviousBudget = false;
    public hasNextBudget = false;
    public budgetNavPositionText = '';

    constructor(
        private dataService: DataService,
        private route: ActivatedRoute,
        private router: Router,
        private budgetListTableService: BudgetListTableService
    ) {
    }


    ngOnInit() {
        this.budgetListTableService.quickBudgetListIds$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                if (this.budgetId) {
                    this.updateBudgetNavigationState(this.budgetId);
                }
            });

        this.budget$ = this.route.paramMap.pipe(
            switchMap((params) => {
                const budgetId = params.get('budgetId');
                this.updateBudgetNavigationState(budgetId);
                return this.dataService.getSimpleBudget(budgetId);
            }),
            takeUntil(this.unsubscribe$));

        this.route.paramMap.pipe(
            switchMap((params) => {
                this.budgetId = params.get('budgetId');
                this.updateBudgetNavigationState(this.budgetId);
                return this.dataService.getBudgetSummaryBalance(this.budgetId);
            }))
            .pipe(takeUntil(this.unsubscribe$), tap(s => console.log(s)))
            .subscribe(this.summaryBalance$);
    }

    public ngOnDestroy() {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    public goToPreviousBudget() {
        if (!this.hasPreviousBudget) {
            return;
        }
        const ids = this.budgetListTableService.getQuickBudgetListIds();
        const currentIndex = ids.indexOf(this.budgetId);
        if (currentIndex > 0) {
            const prevId = ids[currentIndex - 1];
            this.router.navigate(['/balances/summary', prevId]);
        }
    }

    public goToNextBudget() {
        if (!this.hasNextBudget) {
            return;
        }
        const ids = this.budgetListTableService.getQuickBudgetListIds();
        const currentIndex = ids.indexOf(this.budgetId);
        if (currentIndex !== -1 && currentIndex < ids.length - 1) {
            const nextId = ids[currentIndex + 1];
            this.router.navigate(['/balances/summary', nextId]);
        }
    }

    private updateBudgetNavigationState(budgetId: string) {
        const ids = this.budgetListTableService.getQuickBudgetListIds();
        if (!ids || ids.length === 0 || !budgetId) {
            this.hasPreviousBudget = false;
            this.hasNextBudget = false;
            this.budgetNavPositionText = '';
            return;
        }
        const index = ids.indexOf(budgetId);
        if (index === -1) {
            this.hasPreviousBudget = false;
            this.hasNextBudget = false;
            this.budgetNavPositionText = '';
            return;
        }
        this.hasPreviousBudget = index > 0;
        this.hasNextBudget = index < ids.length - 1;
        this.budgetNavPositionText = `${index + 1} / ${ids.length}`;
    }

    compare(a: number, b: number) {
        return a > b;
    }
}